/**
 * Dates for people, not debug output (audit P2-13). DATE columns travel as `YYYY-MM-DD`; timestamps as
 * ISO. Everything renders through Intl in the person's locale, and "today" is the local calendar day,
 * so an item due today is not overdue at 5 pm Pacific because UTC rolled over.
 */
const DATE = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
const DATE_NO_YEAR = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })
const DATE_TIME = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
const TIME = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })

/** `YYYY-MM-DD` → a local Date at noon (no timezone drift across the day boundary). */
export function localDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number) as [number, number, number]
  return new Date(y, m - 1, d, 12)
}

/** Today's local calendar day as `YYYY-MM-DD`. */
export function todayLocal(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** `2026-09-05` → `Sep 5, 2026` (year dropped when it is this year). */
export function fmtDate(ymd: string | null | undefined, opts: { year?: 'always' | 'auto' } = {}): string {
  if (!ymd)
    return ''
  const d = localDate(ymd.slice(0, 10))
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return (opts.year === 'always' || !sameYear ? DATE : DATE_NO_YEAR).format(d)
}

/** ISO timestamp → `Sep 5, 2026, 3:12 PM`, or just the time when it is today. */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso)
    return ''
  const d = new Date(iso)
  return d.toDateString() === new Date().toDateString() ? TIME.format(d) : DATE_TIME.format(d)
}

/** True when a `YYYY-MM-DD` due date is before today (local calendar). */
export function isOverdue(dueOn: string | null | undefined, today = todayLocal()): boolean {
  return Boolean(dueOn) && dueOn! < today
}

/** "5th", "21st": the day of month the way the Home rows and the close-day placeholder say it. */
export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`
}
